import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Link,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import denzad9 from '../images/byd-denza-d9-side.jpeg'; // Pastikan path benar
import PageSeo, {
  articleJsonLd,
  breadcrumbJsonLd,
  keywordSets,
  productJsonLd,
} from '../PageSeo';

function DenzaD9Detail() {
  const priceValue = 1080000000;
  const formattedPrice = 'Rp1.080.000.000';

  const pageTitle = `Danza D9 NIK 2026 Indonesia | Harga ${formattedPrice} dan Spesifikasi`;
  const pageDescription = `Cek Danza D9 NIK 2026 di BYD BSD Serpong. Harga ${formattedPrice} OTR Jakarta, MPV listrik premium dengan Blade Battery Gen 2, 340 kW, jarak tempuh 730 km NEDC, interior baru Black, promo, dan test drive.`;

  return (
    <Box
      sx={{ padding: { xs: 2, sm: 4 }, maxWidth: '1200px', margin: '0 auto' }}
    >
      <PageSeo
        title={pageTitle}
        description={pageDescription}
        keywords={[
          ...keywordSets.localDealer,
          'Danza D9 Indonesia',
          'harga Danza D9',
          'MPV listrik mewah',
          'Danza D9 BSD Serpong',
          'test drive Danza D9',
        ]}
        path='/blog/denza-d9-indonesia-mpv-mewah'
        image={denzad9}
        type='article'
        jsonLd={[
          productJsonLd({
            name: 'Danza D9',
            description: pageDescription,
            image: denzad9,
            path: '/blog/denza-d9-indonesia-mpv-mewah',
            price: priceValue,
          }),
          articleJsonLd({
            title: pageTitle,
            description: pageDescription,
            image: denzad9,
            path: '/blog/denza-d9-indonesia-mpv-mewah',
          }),
          breadcrumbJsonLd([
            { name: 'Beranda', path: '/' },
            { name: 'Blog', path: '/blog' },
            { name: 'Danza D9', path: '/blog/denza-d9-indonesia-mpv-mewah' },
          ]),
        ]}
      />

      <Box sx={{ mt: 10 }}>
        <Typography
          variant='h3'
          component='h1'
          fontWeight='bold'
          gutterBottom
          sx={{ fontSize: { xs: '2rem', md: '3rem' } }}
        >
          Danza D9 Indonesia: Solusi MPV Mewah, Bebas Ganjil Genap
        </Typography>
        <Box
          component='img'
          src={denzad9}
          alt='Tampilan samping BYD Danza D9 Indonesia yang mewah dengan desain futuristik'
          sx={{
            width: '100%',
            height: 'auto',
            borderRadius: 2,
            mb: 4,
            boxShadow: 3,
          }}
        />

        <Typography
          variant='body1'
          paragraph
          sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}
        >
          Kehadiran <strong>BYD Danza D9</strong> di Indonesia memberikan
          standar baru bagi pecinta MPV premium. Tidak hanya menawarkan
          kemewahan sekelas First Class, mobil ini merupakan solusi cerdas untuk
          mobilitas perkotaan karena fasilitas{' '}
          <strong>Bebas Ganjil Genap</strong> di jalanan protokol Jakarta.
          Selain itu, pemilik juga akan menikmati keuntungan finansial berupa{' '}
          <strong>Pajak Kendaraan Listrik Murah</strong> yang jauh lebih efisien
          dibanding mobil bensin konvensional.
        </Typography>
        {/* Section Harga & Spesifikasi */}
        <Typography
          variant='h4'
          component='h2'
          fontWeight='bold'
          sx={{ mt: 5, mb: 3 }}
        >
          Harga & Spesifikasi Danza D9 NIK 2026 (FWD)
        </Typography>
        <TableContainer component={Paper} elevation={2} sx={{ mb: 4 }}>
          <Table>
            <TableHead sx={{ backgroundColor: '#1976d2' }}>
              <TableRow>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                  Fitur
                </TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                  Detail Spesifikasi
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  <strong>Harga OTR (Mulai)</strong>
                </TableCell>
                <TableCell>
                  <Typography color='primary' fontWeight='bold'>
                    {formattedPrice}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Penggerak</TableCell>
                <TableCell>FWD (motor tunggal)</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Tenaga Maksimum</TableCell>
                <TableCell>340 kW (sebelumnya 230 kW)</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Torsi Maksimum</TableCell>
                <TableCell>365 Nm</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Kapasitas Baterai</TableCell>
                <TableCell>115 kWh (Blade Battery Generasi 2)</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Jarak Tempuh (NEDC)</TableCell>
                <TableCell>730 km</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Akselerasi 0-100 km/jam</TableCell>
                <TableCell>8,5 detik</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Pengisian Super Flash Charging</TableCell>
                <TableCell>0-97% sekitar 9 menit</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Suspensi</TableCell>
                <TableCell>DiSus-C</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Audio</TableCell>
                <TableCell>Devialet</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Airbag</TableCell>
                <TableCell>9</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Kapasitas Bagasi</TableCell>
                <TableCell>689 liter</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Wheelbase</TableCell>
                <TableCell>3.110 mm</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Pilihan Interior</TableCell>
                <TableCell>Beige, Brown, Black (baru)</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        {/* LSI Content Section */}
        <Grid container spacing={4} sx={{ mb: 5 }}>
          <Grid item xs={12} md={6}>
            <Typography
              variant='h5'
              component='h3'
              fontWeight='bold'
              gutterBottom
            >
              Keunggulan Pajak & Operasional
            </Typography>
            <Typography variant='body1' sx={{ lineHeight: 1.8 }}>
              Salah satu alasan utama mengapa pengusaha dan keluarga beralih ke
              Danza D9 adalah <strong>Pajak Kendaraan Listrik Murah</strong>.
              Dengan insentif pemerintah, PKB (Pajak Kendaraan Bermotor) untuk
              mobil listrik murni hanya berkisar di angka ratusan ribu rupiah
              per tahun. Ini adalah penghematan luar biasa untuk unit MPV mewah.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography
              variant='h5'
              component='h3'
              fontWeight='bold'
              gutterBottom
            >
              Kemudahan Charging Station di BSD
            </Typography>
            <Typography variant='body1' sx={{ lineHeight: 1.8 }}>
              Bagi Anda yang tinggal di Tangerang Selatan, infrastruktur{' '}
              <strong>Charging Station BSD</strong> kini sudah sangat tersebar
              luas, mulai dari mal ternama hingga area publik. Mengisi daya
              Danza D9 dengan kapasitas baterai 115 kWh menjadi sangat praktis,
              baik melalui wallbox di rumah maupun stasiun pengisian cepat
              (SPKLU).
            </Typography>
          </Grid>
        </Grid>
        {/* Optimasi Gambar Ke-2 */}
        <Box
          component='img'
          src={denzad9}
          alt='Interior baris kedua Danza D9 Indonesia yang mewah dengan Captain Seat dan fitur pijat'
          sx={{ width: '100%', height: 'auto', borderRadius: 2, mb: 4 }}
        />
        {/* Internal Linking Section */}
        <Paper
          elevation={0}
          sx={{ p: 3, backgroundColor: '#f5f5f5', borderRadius: 2, mb: 5 }}
        >
          <Typography variant='h6' fontWeight='bold' gutterBottom>
            Baca Juga Artikel Terkait:
          </Typography>
          <Typography variant='body2'>
            •{' '}
            <Link
              component={RouterLink}
              to='/blog/spesifikasi-lengkap-byd-sealion-7'
            >
              Spesifikasi Lengkap BYD Sealion 7
            </Link>
            <br />•{' '}
            <Link component={RouterLink} to='/pricing'>
              Update Daftar Harga (Price List) Mobil BYD 2026
            </Link>
            <br />•{' '}
            <Link
              component={RouterLink}
              to='/blog/keunggulan-teknologi-byd-sealion-7'
            >
              Inovasi Teknologi Blade Battery BYD
            </Link>
          </Typography>
        </Paper>
        {/* CTA */}
        <Paper
          sx={{
            p: 4,
            textAlign: 'center',
            background: 'linear-gradient(45deg, #1976d2 30%, #21CBF3 90%)',
            color: '#fff',
          }}
        >
          <Typography variant='h4' fontWeight='bold' gutterBottom>
            Miliki Danza D9 Sekarang!
          </Typography>
          <Typography variant='body1' sx={{ mb: 3 }}>
            Konsultasikan simulasi kredit, ketersediaan unit, dan jadwal test
            drive bersama spesialis kami.
          </Typography>
          <Button
            variant='contained'
            color='secondary'
            size='large'
            href='https://wa.me/6281286864825'
            target='_blank'
            sx={{ fontWeight: 'bold', px: 5 }}
          >
            Hubungi Sales Specialist (Junius)
          </Button>
        </Paper>
      </Box>
    </Box>
  );
}

export default DenzaD9Detail;
